import { ZmyleErrorId } from 'utils/constants';
import { ErrorWhitelist } from 'services/errorWhitelist';

import { isAxiosError, isZmyleError } from './types';

const filterErrorIds: ZmyleErrorId[] = [
  ZmyleErrorId.NO_SUCH_GIFTCARD,
  ZmyleErrorId.MALFORMED_ID,
  ZmyleErrorId.REQUIRES_AUTHENTICATION,
  ZmyleErrorId.INVALID_PINCODE,
  ZmyleErrorId.NO_SUCH_GIFTCARD_CONTEXT,
] as const;

const defaultErrorIdsWhitelist: ZmyleErrorId[] = [
  ZmyleErrorId.TOO_MANY_REQUESTS,
  ZmyleErrorId.NO_SUCH_NETWORK,
] as const;

const filterErrorMessages = [
  /network error/gi,
  /no error has been set/gi,
  /no such giftcard checkout context/gi,
  /pincode contains invalid characters/gi,
  /giftcard \w+ has insufficient funds/gi,
  /Request failed with status code 403/gi,
  /Camera not found/gi,
] as const;

export const shouldFilterError = async (error: unknown): Promise<boolean> => {
  if (isAxiosError(error)) {
    if (error.response?.data && isZmyleError(error.response.data)) {
      const { errorId, errorMsg, status } = error.response.data;

      if (defaultErrorIdsWhitelist.includes(errorId)) return false;

      const { isErrorWhitelisted } = ErrorWhitelist.getInstance();

      const isWhitelisted = await isErrorWhitelisted(errorId);

      if (isWhitelisted) return false;

      return (
        (status >= 400 && status < 500) ||
        filterErrorIds.includes(errorId) ||
        filterErrorMessages.some(filter => filter.test(errorMsg))
      );
    }
  }

  if (isZmyleError(error)) {
    const { errorId, errorMsg, status } = error;

    if (defaultErrorIdsWhitelist.includes(errorId)) return false;

    const { isErrorWhitelisted } = ErrorWhitelist.getInstance();

    const isWhitelisted = await isErrorWhitelisted(errorId);

    if (isWhitelisted) return false;

    return (
      (status >= 400 && status < 500) ||
      filterErrorIds.includes(errorId) ||
      filterErrorMessages.some(filter => filter.test(errorMsg))
    );
  }

  if (error instanceof Error) {
    return filterErrorMessages.some(filter => filter.test(error.message));
  }

  return false;
};

export const getErrorNameByErrorId = (errorId: ZmyleErrorId): string => {
  const errorGroup: Partial<Record<ZmyleErrorId, string>> = {
    [ZmyleErrorId.MALFORMED_URL]: 'Malformed URL',
    [ZmyleErrorId.TOO_MANY_REQUESTS]: 'Too many requests',
  };

  return errorGroup?.[errorId] || 'Error';
};

export const buildErrorInstanceFromZmyleError = (error: ZmyleError): Error => {
  const { errorMsg, errorId, status } = error;

  const errorInstance = new Error(errorMsg);

  const errorName = getErrorNameByErrorId(errorId as ZmyleErrorId);

  errorInstance.name = status >= 400 && status < 500 ? errorId : errorName;

  if (errorInstance.name === 'Error') errorInstance.name = errorMsg;

  return errorInstance;
};
